import { Injectable } from '@angular/core';
import { StrummingService, StrumSet } from './strumming.service';
import { ChordScale, NoteToPlay } from './sound';

export type Schema = 'Arpeggio' | 'ABAB' | 'ABBB' | 'All' | 'ABB' | 'Struming';

@Injectable({
  providedIn: 'root'
})

export class ChordsSchemasService {

  constructor(
    public  strummingService: StrummingService
  ) { }
  excludeNotes(structure: number[], max: number) {
    if (structure.length <= max) {
      return structure;
    }
    const result = [...structure];
    const toExclude = [
      0,  // root
      7,  // fifth
      10, // 7b
      11, // seventh
      14, // ninth
      8,  // aug fifth
      6,  // dim fifth
      1, 2, 3, 4, 5, 9, 12, 13 // others
    ];
    let toExcludeIndex = 0;
    while ((result.length > max) && (toExcludeIndex < toExclude.length)) {
      const excludeIndex = result.indexOf(toExclude[toExcludeIndex]);
      if (excludeIndex !== -1) {
        result.splice(excludeIndex, 1);
      }
      toExcludeIndex++;
    }
    return result;
  }
  playChordFromSchema(
    schema: Schema,
    chord: ChordScale,
    volume: number,
    repeat: number,
    bpm: number,
    strum?: StrumSet
  ): NoteToPlay[] {

    if (schema === 'Arpeggio') {
      return this.playChordArpeggio(chord, volume, repeat, bpm);
    } else if (schema === 'ABAB') {
      return this.playChordAB(1, chord, volume, repeat, bpm);
    } else if (schema === 'ABBB') {
      return this.playChordAB(3, chord, volume, repeat, bpm);
    } else if (schema === 'ABB') {
      return this.playChordAB(2, chord, volume, repeat, bpm);
    } else if (schema === 'All') {
      return this.playChordAll(chord, volume, repeat, bpm);
    } else if (schema === 'Struming') {
      return this.playChordStruming(chord, volume, strum as StrumSet, repeat, bpm);
    }
    return [];
  }
  beat(bpm: number): number {
    return 60000 / bpm;
  }
  schemaTime(schema: Schema, bpm: number, strum: StrumSet): number {
    if (schema === 'ABAB') {
      return 2 * this.beat(bpm);
    } else if (schema === 'ABB') {
      return 3 * this.beat(bpm);
    } else if (schema === 'Struming') {
      return 4 * this.beat(bpm);
    }
    return 4 * this.beat(bpm);
  }
  playChordArpeggio(
    chord: ChordScale,
    volume: number,
    repeat: number,
    bpm: number
  ): NoteToPlay[] {
    const ntp: NoteToPlay[] = [];
    let delta = 0;
    for (let r = 0; r < repeat; r++) {
      // Add Chord Bass
      ntp.push({
        note: chord.note12 + 0,
        delay: delta,
        volume: (.8 + (Math.random() * .2)) * volume
      });
      delta += this.beat(bpm);
      this.excludeNotes(chord.structure, 3).forEach((interval) => {
        ntp.push({
          note: chord.note12 + 12 + interval,
          delay: delta,
          volume: (.8 + (Math.random() * .2)) * volume
        });
        delta += this.beat(bpm);
      });
    }
    return ntp;
  }
   playChordAB(numberOfB: number, chord: ChordScale, volume: number, repeat: number, bpm: number) {
    const ntp: NoteToPlay[] = [];
    let delta = 0;
    for (let r = 0; r < repeat; r++) {
      ntp.push({
        note: chord.note12 + 0,
        delay: delta,
        volume: (.8 + (Math.random() * .2)) * volume
      });
      for (let i = 0; i < numberOfB; i++) {
        delta += this.beat(bpm);
        this.excludeNotes(chord.structure, 4).forEach(interval => {
          ntp.push({
            note: chord.note12 + 12 + interval,
            delay: delta,
            volume: (.5 + (Math.random() * .5)) * volume * .8
          });
        });
      }
      delta += this.beat(bpm);
    }
    return ntp;
  }
  playChordAll(chord: ChordScale, volume: number, repeat: number, bpm: number) {
    const ntp: NoteToPlay[] = [];
    let delta = 0;
    for (let r = 0; r < repeat; r++) {
      for (let i = 0; i < 2; i++) {
        ntp.push({
          note: chord.note12 + 0,
          delay: delta,
          volume: (.5 + (Math.random() * .5)) * volume
        });
        this.excludeNotes(chord.structure, 4).forEach(interval => {
          ntp.push({
            note: chord.note12 + 12 + interval,
            delay: delta,
            volume: (.5 + (Math.random() * .5)) * volume
          });
        });
        delta += (2 * this.beat(bpm));
      }
    }
    return ntp;
  }
  playChordStruming(
    chord: ChordScale,
    volume: number,
    strum: StrumSet,
    repeat: number,
    bpm: number,
    humanRate = 10
  ) {
    const ntp: NoteToPlay[] = [];
    let delta = 0;
    for (let r = 0; r < repeat; r++) {
      for (let rs = 0; rs < strum.repeatStructure; rs++) {
        for (let i = 0; i < strum.structure.length; i++) {
          const hit = strum.structure[i];
          if (hit !== '_') {

            let notes = chord.noteNumbers.slice(); // clone for reverse
            if (hit.toLowerCase() === 'u') {
              notes.reverse();
            }
            let inChordDelta = 0;
            const accent = (['U', 'D'].indexOf(hit) !== -1) ? 1.5 : 1; // +50%
            const randomAccent = (.8 + (Math.random() * .4)); // +- 20%
            chord.noteNumbers.forEach(noteNumber => {
              ntp.push({
                note: noteNumber,
                delay: delta + inChordDelta,
                volume:  randomAccent * volume * accent
              });
              inChordDelta += this.strummingService.stringHumanizer(bpm, humanRate);
            });
          }
          delta += 4 * this.beat(bpm) / (strum.structure.length * strum.repeatStructure);
        }
      }
    }
    return ntp;
  }
}
